//@ts-nocheck
import React, { useRef, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Cesium from "cesium";
import { setAPIcalled, setSimulationClockFlag, setMapLoading } from '../../../../store/UnifiedControlPlaneSlice'
import Loader from "../../../../Layouts/Loader/Loader";
import { ucp_visualization_presentation } from "../CesiumUtils/Visualizations";
import { setup_gs_hover } from "../../../../utils/commonFunctions";
import GroundStationCard from "../../../CommonComponents/GroundStationCard";
import GroundTargetCard from "../../../CommonComponents/GroundTargetCard";

export const getGroundStationById = (groundStations, groundStationId) => {
    const groundStationData = groundStations.find(station => `gs_${station.groundStationId}` === groundStationId)
    return groundStationData;
};

export const getTargetbyId = (ordersData, targetId) => {
    const allOrders = [
        ...ordersData.executed,
        ...ordersData.current,
        ...ordersData.pending,
    ];
    const groundStationData = allOrders.find(order => `aoi_${order?.spec?.geofeature?.features[0]?.id}` === targetId)
    return groundStationData;
};

const formatTargetData = (targetOrderData) => {
    const formatData = {
        areaOfInterestId: targetOrderData?.spec?.geofeature?.features[0]?.id,
        name: targetOrderData?.spec.name,
        description: targetOrderData?.spec?.geofeature?.label,
        type: "Target Track",
        areaOfInterestManagerId: "", // not applicable in ucp order details
        coordinates: {
            "altitude": 0,
            "lat": targetOrderData?.spec?.geofeature?.features[0]?.geometry?.coordinates[0][0][1],
            "long": targetOrderData?.spec?.geofeature?.features[0]?.geometry?.coordinates[0][0][0],
            "minElevationAngle": 0
        },
        polygon: null,
        missionId: targetOrderData?.spec?.missionId,
        tenantId: "",
        createdAt: targetOrderData?.createdAt,
        updatedAt: targetOrderData?.updatedAt,
    }
    return formatData;
}

const MissionOrbit = ({ ucpViewer, setUcpViewer }) => {

    const dispatch = useDispatch()

    const [selected_gs, set_selected_gs] = useState(null);
    const [selected_target, set_selected_target] = useState(null);

    const groups = useSelector(state => state.unifiedControlPlane.groups);
    const allGroundStations = useSelector(state => state.unifiedControlPlane.allGroundStations);
    const orders = useSelector(state => state.unifiedControlPlane.orders);
    const simulationClockFlag = useSelector(state => state.unifiedControlPlane.simulationClockFlag)
    const simulationCurrentTime = useSelector(state => state.unifiedControlPlane.scenarioCurrentTime);
    const mapLoading = useSelector(state => state.unifiedControlPlane.mapLoading)

    const groupsRef = useRef(groups);
    const allGroundStationsRef = useRef(allGroundStations);
    const ordersRef = useRef(orders);
    const handlerRef = useRef(null);
    const gsHoverHandlerRef = useRef(null);

    useEffect(() => {
        if (simulationClockFlag) { // After retrieving the current time, update Cesium's clock accordingly
            try {
                if (ucpViewer && ucpViewer.clock && simulationCurrentTime) {
                    const startTime = Cesium.JulianDate.fromIso8601(simulationCurrentTime);
                    ucpViewer.clock.startTime = startTime.clone(); //set cesium clock start time
                    ucpViewer.clock.currentTime = startTime.clone(); //set cesium clock current time
                    ucpViewer.clock.shouldAnimate = true;
                    dispatch(setSimulationClockFlag(false));
                    dispatch(setAPIcalled(true));
                }
            } catch (err) {
                console.error('Failed to get simulation time')
            } finally {
                dispatch(setMapLoading(false))
            }
        }
    }, [dispatch, simulationClockFlag, ucpViewer]);

    useEffect(() => {
        groupsRef.current = groups;
    }, [groups]);

    useEffect(() => {
        allGroundStationsRef.current = allGroundStations;
    }, [allGroundStations]);

    useEffect(() => {
        ordersRef.current = orders;
    }, [orders])

    useEffect(() => {
        dispatch(setMapLoading(true))
        let imaginary = new Cesium.MapboxStyleImageryProvider({
            styleId: 'dark-v10',
            accessToken: "pk.eyJ1IjoiYW50YXJpcy1tYXBib3giLCJhIjoiY2x2cGNubzF4MDBveTJtb2RtNG5zMGQ2NCJ9.MkPyl-z2FXuFSyYNwP_oaw"
        });

        const viewer = new Cesium.Viewer("cesiumContainer", {
            requestRenderMode: true,
            shouldAnimate: true,
            animation: false, // Disable the default animation widget
            baseLayerPicker: false, // Disable the default base layer picker
            timeline: false, // Disable the timeline
            geocoder: false, // Disable the geocoder
            homeButton: false, // Disable the home button
            navigationHelpButton: false, // Disable the navigation help button
            sceneModePicker: false, // Disable the scene mode picker
            selectionIndicator: false, // Disable the selection indicator
            fullscreenButton: false,
            targetFrameRate: 10,
            imageryProvider: imaginary,
        });

        viewer.scene.globe.baseColor = Cesium.Color.WHITE; // Set a base color for the globe
        viewer.scene.globe.enableLighting = false; // Enable lighting to ensure consistent shading
        viewer.scene.globe.showGroundAtmosphere = false; // Show the atmospheric effects
        viewer.scene.gamma = 2.0; // Adjust the value based on your observation
        viewer.scene.skyAtmosphere.show = false;

        viewer.clock.currentTime = viewer.clock.startTime;
        viewer.clock.shouldAnimate = true;
        viewer.clock.clockRange = Cesium.ClockRange.LOOP_STOP;
        viewer.scene.screenSpaceCameraController.enableRotate = true;
        // viewer.scene.screenSpaceCameraController.enableTranslate = true;
        viewer.scene.screenSpaceCameraController.enableZoom = true;
        viewer.scene.screenSpaceCameraController.enableTilt = true;
        viewer.scene.screenSpaceCameraController.enableLook = true;

        viewer.scene.morphTo2D(0);

        viewer.camera.flyTo({
            // Use flyTo instead of setView
            destination: Cesium.Cartesian3.fromDegrees(0, 0, 37000000), // Set the initial view position to be full view
            orientation: {
                heading: Cesium.Math.toRadians(10),
                pitch: Cesium.Math.toRadians(-90),
                roll: 0,
            },
        });

        gsHoverHandlerRef.current = setup_gs_hover(viewer, 'GS_Object');

        handlerRef.current = new Cesium.ScreenSpaceEventHandler(viewer?.scene?.canvas);
        handlerRef.current.setInputAction((click) => {
            const pickedObject = viewer?.scene?.pick(click.position);
            if (Cesium.defined(pickedObject) && Cesium.defined(pickedObject.id)) {
                let entity = pickedObject.id;
                const dataSourceName = entity?.entityCollection?.owner?.name;
                const description = (entity && entity.description) && entity.description.getValue(Cesium.JulianDate.now());
                if (dataSourceName === 'Orbit_Object') {
                    const satelliteId = entity._id;
                    if (entity && entity.path && entity.path.material && satelliteId) {
                        ucp_visualization_presentation().selected_satellite(satelliteId, viewer);
                    }
                }
                if (dataSourceName === 'GS_Object') {
                    const gsId = entity._id;
                    const station = getGroundStationById(allGroundStationsRef.current, gsId);
                    set_selected_gs(station);
                    ucp_visualization_presentation().highlightGroundStations([gsId], viewer);
                }
                if (description === 'target') {
                    const targetId = entity._id;
                    const targetData = getTargetbyId(ordersRef.current, targetId);
                    ucp_visualization_presentation().highlightGroundTargets([targetId], viewer)
                    const formattedTargetData = formatTargetData(targetData);
                    set_selected_target(formattedTargetData);
                }
            } else {
                ucp_visualization_presentation().unselect_satellites(viewer, groupsRef.current);
                set_selected_gs(null);
                set_selected_target(null);
                ucp_visualization_presentation().highlightGroundStations([], viewer);
                ucp_visualization_presentation().highlightGroundTargets([], viewer);
            }
        }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

        viewer.cesiumWidget.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        setUcpViewer(viewer)

        return () => {
            gsHoverHandlerRef.current?.destroy();
            viewer.destroy();
            dispatch(setAPIcalled(false))
        };
    }, [dispatch, setUcpViewer]);

    return (
        <>
            {mapLoading && <Loader />}
            <div id="cesiumContainer" className="position-relative" style={{ height: "100vh", position: "relative", bottom: "10px" }} >
            </div>
            {selected_gs && <GroundStationCard station={selected_gs} viewer={ucpViewer} />}
            {selected_target && <GroundTargetCard target={selected_target} viewer={ucpViewer} />}
        </>
    );
};

export default MissionOrbit;
